import Button from '@/common/components/Button';
import Modal from '@/common/components/Modal';
import SelectController from '@/common/components/SelectController';
import { useCalendarTimezones } from '@/common/hooks/queries/useCalendarTimezones';
import { CompanyData } from '@/features/companies/types/companies.type';
import { SettingsIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import useUpdateCompany from '../../hooks/useUpdateCompany';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { companySchema, CompanyValidationSchema } from '../../schemas/create.schema';
import useDebounce from '@/common/hooks/useDebounce';
import { Mapper } from '@/common/services/mapper';
import { CalendarTimezoneTransformer, formatTimezoneAcronym } from '@/common/services/calendarTimezoneTransformer';
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple';
import { useQueryClient } from '@tanstack/react-query';

interface CompanyTimezoneSectionProps {
  company?: CompanyData;
  isCompanyPending?: boolean;
}

export const CompanyTimezoneSection = ({ company, isCompanyPending }: CompanyTimezoneSectionProps) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const {
    mutate: updateCompany,
    isPending: isUpdateCompanyPending,
    isError: isUpdateCompanyError,
  } = useUpdateCompany({
    companyId: company?.id,
    onSuccess: () => {
      setMessages(['Fuso horário atualizado com sucesso']);
      queryClient.invalidateQueries({ queryKey: ['company', company?.id] });
      setIsEditModalOpen(false);
    },
    onError: (error) => {
      setMessages(error.response?.data.errors.base || ['Ocorreu um erro ao atualizar fuso horário']);
    },
  });

  const { control, reset, handleSubmit } = useForm<CompanyValidationSchema>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      time_zone_id: company?.time_zone ? CalendarTimezoneTransformer.optionsTransformer()([company.time_zone])[0] : undefined,
    },
  });

  const onSubmit: SubmitHandler<CompanyValidationSchema> = (data) => updateCompany(data);

  const [searchTimezone, setSearchTimezone] = useDebounce('');

  const { data: timezones } = useCalendarTimezones({
    filters: { search: searchTimezone },
    mapper: Mapper.mapWithTransformer({ transformer: CalendarTimezoneTransformer.optionsTransformer() })
  });

  useEffect(() => {
    reset({
      time_zone_id: company?.time_zone ? CalendarTimezoneTransformer.optionsTransformer()([company.time_zone])[0] : undefined,
    });
  }, [isCompanyPending]);

  return (
    <>
      <div className="bg-white-200 rounded-lg p-4 space-y-2">
        <p className="text-[11px] leading-3">Fuso horário da empresa</p>

        <div className="flex items-center gap-2">
          <p className="text-sm">
            {company?.time_zone ? `${company.time_zone.display_name || company.time_zone.identifier} (GMT${formatTimezoneAcronym(company.time_zone.acronym)})` : 'Não definido'}
          </p>

          <Button variant="link-white" onClick={() => setIsEditModalOpen(true)}>
            <SettingsIcon strokeWidth={1} className="size-4" />
          </Button>
        </div>
      </div>

      <Modal
        isOpen={isEditModalOpen}
        setIsOpen={setIsEditModalOpen}
        title="Editar fuso horário da empresa"
        body={(
          <div className='text-white-800'>
            <SelectController
              control={control}
              name="time_zone_id"
              label="Fuso horário"
              options={timezones}
              onInputChange={setSearchTimezone}
            />
          </div>
        )}
        useCancellableFooter
        cancelButtonClassName='bg-white-100'
        confirmButtonClassName='bg-primary-blue'
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonIsLoading={isUpdateCompanyPending}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={isUpdateCompanyError}
        />
      )}
    </>
  );
};
